/**
 * JsonRenderer - Visualizador interativo de JSON em sandbox.
 *
 * Exibe JSON com cores por tipo e nós colapsáveis.
 *
 * IMPORTANTE: não usa inline event handlers (onclick="...") pois são bloqueados
 * pelo CSP do sandbox. Toda interatividade é via event delegation no container.
 */

export class JsonRenderer {
    static renderToHtml(source: string): string {
        let parsed: unknown;
        try {
            parsed = JSON.parse(source);
        } catch (e: any) {
            return `<!DOCTYPE html><html><body style="margin:0;padding:16px;font-family:system-ui,sans-serif;">
<div style="color:#dc2626;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:16px;font-family:monospace;font-size:13px;">
  <strong>JSON inválido</strong><br>${JsonRenderer.escapeForAttr(e.message)}
</div></body></html>`;
        }

        // Serializa de volta para garantir formato limpo e escapar caracteres especiais
        const safeJson = JSON.stringify(parsed)
            .replace(/\\/g, '\\\\')
            .replace(/`/g, '\\`')
            .replace(/\$/g, '\\$');

        return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 13px; line-height: 1.6; background: transparent; color: #374151; }
body { padding: 16px; }

.json-key     { color: #7c3aed; font-weight: 600; }
.json-string  { color: #15803d; }
.json-number  { color: #1d4ed8; }
.json-boolean { color: #c2410c; font-weight: 600; }
.json-null    { color: #9ca3af; font-style: italic; }

@media (prefers-color-scheme: dark) {
  html, body { color: #e2e8f0; }
  .json-key     { color: #a78bfa; }
  .json-string  { color: #4ade80; }
  .json-number  { color: #60a5fa; }
  .json-boolean { color: #fb923c; }
  .json-null    { color: #94a3b8; }
}

.json-node    { margin-left: 20px; }

.toggle {
    cursor: pointer;
    user-select: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-right: 2px;
    color: #9ca3af;
    font-size: 9px;
    transition: transform 0.15s;
    vertical-align: middle;
}
.toggle::before { content: '▼'; }
.toggle.collapsed::before { transform: rotate(-90deg); display: block; }

.json-children          { display: block; }
.json-children.collapsed { display: none; }
</style>
</head>
<body>
<div id="json-root"></div>
<script>
(function () {
    var data = JSON.parse(\`${safeJson}\`);

    function esc(s) {
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function buildTree(val, isLast) {
        var comma = isLast ? '' : ',';

        if (val === null)            return '<span class="json-null">null</span>' + comma;
        if (typeof val === 'string') return '<span class="json-string">"' + esc(val) + '"</span>' + comma;
        if (typeof val === 'number') return '<span class="json-number">' + val + '</span>' + comma;
        if (typeof val === 'boolean') return '<span class="json-boolean">' + val + '</span>' + comma;

        if (Array.isArray(val)) {
            if (val.length === 0) return '[]' + comma;
            var inner = val.map(function (item, i) {
                return '<div class="json-node">' + buildTree(item, i === val.length - 1) + '</div>';
            }).join('');
            return '<span class="toggle" data-toggle></span>[<div class="json-children">' + inner + '</div>]' + comma;
        }

        if (typeof val === 'object') {
            var keys = Object.keys(val);
            if (keys.length === 0) return '{}' + comma;
            var pairs = keys.map(function (k, i) {
                return '<div class="json-node"><span class="json-key">"' + esc(k) + '"</span>: ' + buildTree(val[k], i === keys.length - 1) + '</div>';
            }).join('');
            return '<span class="toggle" data-toggle></span>{<div class="json-children">' + pairs + '</div>}' + comma;
        }

        return esc(String(val));
    }

    document.getElementById('json-root').innerHTML = buildTree(data, true);

    // Event delegation — sem inline onclick (bloqueado por CSP)
    document.getElementById('json-root').addEventListener('click', function (e) {
        var target = e.target;
        if (!target || !target.hasAttribute('data-toggle')) return;
        target.classList.toggle('collapsed');
        var children = target.nextElementSibling;
        while (children && !children.classList.contains('json-children')) {
            children = children.nextElementSibling;
        }
        if (children) {
            children.classList.toggle('collapsed');
        }
    });
})();
</script>
</body>
</html>`;
    }

    private static escapeForAttr(str: string): string {
        return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
}
